<template>
  <section class="waiver-container" id="pdf-container">
    <header class="waiver-header">
      <HeroLogo />
      <h1>Event Waiver</h1>
      <p>Date: {{ todayUSFormatted }}</p>
    </header>
    <form @submit.prevent="submitForm">
      <p><b>Fields with an asterisk (*) are required</b></p>
      <div class="sided">
        <TheTextfield
          name="first-name"
          label="First Name*"
          placeholder="First Name"
          autocomplete="off"
          :dirty="v$.firstName.$dirty"
          :errorMessage="v$.firstName.$errors[0]?.$message"
          v-model:value="state.firstName"
        />
        <TheTextfield
          name="last-name"
          label="Last Name*"
          autocomplete="off"
          placeholder="Last Name"
          :dirty="v$.lastName.$dirty"
          :errorMessage="v$.lastName.$errors[0]?.$message"
          v-model:value="state.lastName"
        />
      </div>
      <div class="sided">
        <TheTextfield
          type="email"
          name="email"
          autocomplete="off"
          label="Email*"
          placeholder="Enter the email address you registered with"
          :dirty="v$.email.$dirty"
          :errorMessage="v$.email.$errors[0]?.$message"
          v-model:value="state.email"
        />
        <TheTextfield
          name="phone-number"
          label="Phone*"
          placeholder="Enter the phone number you registered with"
          autocomplete="off"
          mask="(000) 000-0000"
          :maxLength="14"
          :dirty="v$.phoneNumber.$dirty"
          :errorMessage="v$.phoneNumber.$errors[0]?.$message"
          v-model:value="state.phoneNumber"
        />
      </div>
      <TheTextfield
        name="street-address"
        :id="!initialState ? 'streetAddress' : 'streetAddressNoSearch'"
        autocomplete="off"
        label="Street Address*"
        placeholder="Enter your street address"
        :dirty="v$.streetAddress.$dirty"
        :errorMessage="v$.streetAddress.$errors[0]?.$message"
        v-model:value="state.streetAddress"
      />
      <div class="sided">
        <TheTextfield
          name="city"
          autocomplete="off"
          label="City*"
          placeholder="Enter your city"
          :dirty="v$.city.$dirty"
          :errorMessage="v$.city.$errors[0]?.$message"
          v-model:value="state.city"
        />
        <NewSelect
          fixedLabel
          filter
          placeholder="Select your state"
          label="State*"
          id="state-select"
          :options="STATE_OPTIONS"
          :dirty="v$.state.$dirty"
          :errorMessage="v$.state.$errors[0]?.$message"
          v-model="state.state"
        />
        <TheTextfield
          name="zip-code"
          autocomplete="off"
          label="Zip Code*"
          placeholder="00000"
          tooltip="Please provide a five or nine digit zip code"
          mask="00000[-0000]"
          :maxLength="10"
          :dirty="v$.zipCode.$dirty"
          :errorMessage="v$.zipCode.$errors[0]?.$message"
          v-model:value="state.zipCode"
        />
      </div>
      <section class="license-details-container">
        <span class="license-title">
          License Details
        </span>
        <div class="sided">
          <TheTextfield
            name="licenseNumber"
            autocomplete="off"
            label="Valid Driver's License*"
            placeholder="Enter your driver's license #"
            :dirty="v$.licenseNumber.$dirty"
            :errorMessage="v$.licenseNumber.$errors[0]?.$message"
            v-model:value="state.licenseNumber"
          />
          <NewSelect
            fixedLabel
            filter
            placeholder="Select state issued"
            label="State Issued*"
            id="issued-state-select"
            :options="STATE_OPTIONS"
            :dirty="v$.licenseState.$dirty"
            :errorMessage="v$.licenseState.$errors[0]?.$message"
            v-model="state.licenseState"
          />
        </div>
        <div class="sided">
          <TheTextfield
            type="date"
            name="licenseExpirationDate"
            label="Expiration Date*"
            placeholder="Expiration Date*"
            :dirty="v$.licenseExpirationDate.$dirty"
            :errorMessage="v$.licenseExpirationDate.$errors[0]?.$message"
            v-model:value="state.licenseExpirationDate"
            :min="todayDate"
          />
          <TheTextfield
            type="date"
            name="dateOfBirth"
            label="Date of Birth*"
            placeholder="Date of Birth*"
            :dirty="v$.licenseOwnerDateofBirth.$dirty"
            :errorMessage="v$.licenseOwnerDateofBirth.$errors[0]?.$message"
            v-model:value="state.licenseOwnerDateofBirth"
            :max="yesterdayDate"
          />
        </div>
      </section>
      <fieldset>
        <legend>Honda Waiver:</legend>
        <div class="waiver-wrapper">
          <WaiverContainer :showFullWaiver="!!props.initialState" ref="hondaWaiverComponent">
            <HondaWaiver :has-child-query="hasChildQuery" />
          </WaiverContainer>
          <TheTextfield
            autocomplete="off"
            name="drivingRecordsExceptions"
            label="You warrant that your motor vehicle driving record contains NO accidents or
            violations with the exception of the following:"
            placeholder="Enter any accidents or violations here"
            v-model:value="state.drivingRecordsExceptions"
          />
          <TheCheckbox
            name="checkHondaWaiver"
            label="Check if you agree to the waiver rules, this counts as an electronic signature.*"
            v-model:value="state.checkAgreeHondaWaiverRules"
            :errorMessage="v$.checkAgreeHondaWaiverRules.$errors[0]?.$message"
          />
        </div>
      </fieldset>
      <fieldset>
        <legend>Event Waiver:</legend>
        <div class="waiver-wrapper">
          <WaiverContainer :showFullWaiver="!!props.initialState" ref="eventWaiverComponent">
            <EventWaiver :has-child-query="hasChildQuery" />
          </WaiverContainer>
          <TheCheckbox
            name="checkEventWaiver"
            label="Check if you agree to the waiver rules, this counts as an electronic signature.*"
            v-model:value="state.checkAgreeEventWaiverRules"
            :errorMessage="v$.checkAgreeEventWaiverRules.$errors[0]?.$message"
          />
        </div>
      </fieldset>
      <div v-show="!props.initialState">
        <button
          class="btn"
          @click.prevent="submitForm()"
          :disabled="savingInProgress"
        >
          <template v-if="!savingInProgress">Submit</template>
          <template v-else>
            <TheLoading />
          </template>
        </button>
      </div>
    </form>
  </section>
</template>

<script setup>
import { useRoute } from 'vue-router';
import HeroLogo from '@/components/svg/HeroLogo.vue';
import TheLoading from '@/components/TheLoading.vue';
import {
  helpers,
  required,
  email,
  sameAs,
} from '@vuelidate/validators';
import useValidate from '@vuelidate/core';
import {
  computed, ref, onMounted, watch,
} from 'vue';
import apiManager from '@/api';
import WaiverContainer from '@/components/eventWaiver/WaiverContainer.vue';
import EventWaiver from '@/components/eventWaiver/waivers/EventWaiver.vue';
import HondaWaiver from '@/components/eventWaiver/waivers/HondaWaiver.vue';
import TheTextfield from '@/components/registration/TheTextfield.vue';
import TheCheckbox from '@/components/registration/TheCheckbox.vue';
import NewSelect from '@/components/global/NewSelect.vue';
import STATE_OPTIONS from '@/constants/states';
import { setDynamicColor } from '@/utils/eventType';
import { Loader } from '@googlemaps/js-api-loader';
import { useToast } from 'vue-toastification';
import { unmaskPhoneNumber } from '@/utils/format';

const props = defineProps({
  initialState: { type: Object, required: false },
});

const route = useRoute();
const hasChildQuery = ref(false);

const state = ref(props.initialState ? props.initialState : {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
  licenseNumber: '',
  licenseState: '',
  licenseExpirationDate: '',
  licenseOwnerDateofBirth: '',
  checkAgreeHondaWaiverRules: false,
  drivingRecordsExceptions: '',
  checkAgreeEventWaiverRules: false,
  WaiverType: 'Normal',
});

const todayDate = ref(new Date().toISOString().split('T')[0]);
const yesterdayDate = computed(() => {
  const today = new Date();
  const yesterday = new Date(today.setDate(today.getDate() - 1));
  return yesterday.toISOString().split('T')[0];
});

const todayUSFormatted = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}).format(new Date());

const pastDateValidator = helpers.withMessage('Expiration Date cannot be in the past', (value) => {
  const inputDate = new Date(value);
  inputDate.setHours(0, 0, 0, 0);
  inputDate.setDate(inputDate.getDate() + 1);
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);
  return inputDate >= currentDate;
});
const futureDateValidator = helpers.withMessage('Date of Birth should be in the past', (value) => {
  const inputDate = new Date(value);
  const currentDate = new Date();
  return inputDate < currentDate;
});
const requiredValidator = helpers.withMessage('Field is required', required);
const emailValidator = helpers.withMessage('Please provide a valid email', email);
const regexPhone = helpers.regex(/^(1[- .]?)?(\([2-9]\d{2}\)[. -]?|[2-9]\d{2}[. -]?)[2-9]\d{2}[- .]?\d{4}$/);
const phoneValidator = helpers.withMessage('Please provide a valid phone number', regexPhone);
const regexZipCode = helpers.regex(/^\d{5}(?:[-]\d{4})?$/);
const zipCodeValidator = helpers.withMessage('Please provide a valid zip code', regexZipCode);
const submissionAttempt = ref(false);

const emit = defineEmits(['sentForm']);

const rules = computed(() => (
  {
    firstName: { requiredValidator },
    lastName: { requiredValidator },
    email: {
      requiredValidator,
      emailValidator,
    },
    phoneNumber: {
      requiredValidator,
      phoneValidator,
    },
    streetAddress: { requiredValidator },
    city: { requiredValidator },
    state: { requiredValidator },
    zipCode: {
      requiredValidator,
      zipCodeValidator,
    },
    licenseNumber: { requiredValidator },
    licenseState: { requiredValidator },
    licenseExpirationDate: { requiredValidator, pastDateValidator },
    licenseOwnerDateofBirth: { requiredValidator, futureDateValidator },
    checkAgreeHondaWaiverRules: {
      sameAs: helpers.withMessage('Please agree to the waiver.', sameAs(true)),
    },
    checkAgreeEventWaiverRules: {
      sameAs: helpers.withMessage('Please agree to the waiver.', sameAs(true)),
    },
  }
));

const toast = useToast();
const v$ = useValidate(rules, state.value, { $autoDirty: true });

const eventWaiverComponent = ref();
const hondaWaiverComponent = ref();

function resetFormValues() {
  state.value.firstName = '';
  state.value.lastName = '';
  state.value.email = '';
  state.value.phoneNumber = '';
  state.value.streetAddress = '';
  state.value.city = '';
  state.value.state = '';
  state.value.zipCode = '';
  state.value.licenseNumber = '';
  state.value.licenseState = '';
  state.value.licenseExpirationDate = '';
  state.value.licenseOwnerDateofBirth = '';
  state.value.checkAgreeHondaWaiverRules = false;
  state.value.drivingRecordsExceptions = '';
  state.value.checkAgreeEventWaiverRules = false;
}

function resetFormDirtyState() {
  const fields = [
    'firstName',
    'lastName',
    'email',
    'phoneNumber',
    'streetAddress',
    'city',
    'state',
    'zipCode',
    'licenseNumber',
    'licenseState',
    'licenseExpirationDate',
    'licenseOwnerDateofBirth',
    'checkAgreeHondaWaiverRules',
    'drivingRecordsExceptions',
    'checkAgreeEventWaiverRules',
  ];

  fields.forEach((field) => {
    if (v$.value[field]) {
      v$.value[field].$dirty = false;
    }
  });
}

function resetForm() {
  setTimeout(() => {
    resetFormValues();
    resetFormDirtyState();
  });
}

const showFullWaivers = ref(false);
const savingInProgress = ref(false);

function submitForm() {
  eventWaiverComponent.value.resetToTop();
  hondaWaiverComponent.value.resetToTop();
  v$.value.$validate();
  submissionAttempt.value = true;
  if (!v$.value.$error) {
    showFullWaivers.value = true;
    savingInProgress.value = true;
    state.value.phoneNumber = unmaskPhoneNumber(state.value.phoneNumber);
    if (!state.value.drivingRecordsExceptions) delete state.value.drivingRecordsExceptions;
    emit('sentForm', state.value);
  } else {
    const message = v$.value.$errors.some((error) => error.$params.type === 'required') ? 'Please provide values for all required fields.' : v$.value.$errors[0].$message;
    toast.error(message);
  }
}

const pdfHasDownloaded = async (base64) => {
  const body = {
    ...state.value,
    pdfData: base64,
  };
  try {
    await apiManager.waiver.postWaiver(body);
    toast.success(`Success! Your waiver is now completed ${state.value.firstName}.`, { timeout: false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    resetForm();
  } catch (err) {
    toast.error(`${err.message}`);
  } finally {
    savingInProgress.value = false;
  }
};

defineExpose({ pdfHasDownloaded });

const autocompleteService = ref();

const onPlaceChanged = () => {
  const { formatted_address: formattedAddress } = autocompleteService.value.getPlace();

  if (!formattedAddress) return;

  const [name, cityName, stateAddress] = formattedAddress.split(',');
  const [stateInitials, zipCodeValue] = stateAddress.trim().split(' ');

  state.value.city = cityName.trim();
  state.value.zipCode = zipCodeValue || '';
  state.value.state = stateInitials || '';
  state.value.streetAddress = name;
};

const activateGoogleAddress = async () => {
  const loader = new Loader({
    apiKey: process.env.VUE_APP_AUTOCOMPLETE_API_KEY,
    version: 'weekly',
    libraries: ['places'],
  });

  const { Autocomplete } = await loader.importLibrary('places');

  autocompleteService.value = new Autocomplete(document.querySelector('#streetAddress'), {
    componentRestrictions: { country: ['US'] },
    types: ['address'],
    fields: ['formatted_address'],
  });

  autocompleteService.value.addListener('place_changed', onPlaceChanged);
};

watch(
  () => route,
  () => {
    if (route.query.child === 'true') {
      hasChildQuery.value = true;
      state.value.WaiverType = 'With Child';
    } else {
      state.value.WaiverType = 'Normal';
    }
  },
);

onMounted(() => {
  if (route.query.child === 'true') {
    hasChildQuery.value = true;
    state.value.WaiverType = 'With Child';
  }
  setDynamicColor('acuratraining');
  setTimeout(() => window.scrollTo(0, 0));
  if (!props.initialState) {
    activateGoogleAddress();
  }
});

</script>

<style lang="scss" scoped>
.waiver-container {
  max-width: 900px;
  margin: 0 auto;
}

.waiver-header {
  margin-bottom: 30px;

  h1 {
    font-weight: 700;
    text-transform: uppercase;
    margin-bottom: 5px;
  }

  svg {
    width: 100%;
    height: auto;
    max-width: 300px;
    margin-bottom: 20px;

    :deep() {
      path:not(#Path_52213),
      #Rectangle_1224,
      #Rectangle_1225,
      #Rectangle_1226 {
        fill: $AC_Navy;
      }
    }
  }
}

form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.sided {
  display: flex;
  flex-direction: column;
  gap: 15px;

  @media (min-width: 780px) {
    flex-direction: row;
    gap: 10px;
  }

  & > :deep(div) {
    flex: 1;
  }
}

.license-details-container {
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 15px 30px;
  margin: 20px 0 30px;

  .license-title {
    font-size: 18px;
    font-weight: 700;
  }
}

fieldset {
  padding: 10px 16px 30px;
  margin-bottom: 20px;
  border-radius: 4px;

  legend {
    font-size: 14px;
    text-transform: uppercase;
  }

  .waiver-wrapper {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
}

.btn-container {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}

.btn {
  text-transform: uppercase;
  color: $AC_Navy;
  padding: 8px 24px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 2px;
  background-color: $AC_Orange;
  border-radius: 4px;
  border: none;
  cursor: pointer;

  &:hover {
    color: $AC_White;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
